import { clampLayout } from '../lib/newbuild-model.js';

/* std-layout.jsx — layout options for a Standard email HTML build.
   Defines window.StdLayout. Rendered inside the standard card in newbuild.jsx.

   Three layout models, then two width dials. The dials are custom pointer
   sliders rather than <input type=range>: they snap to detents at the widths
   people actually ship (600 / 640 / 700, 320 / 375 / 414), tick every 10px with
   a small knob pulse so dragging feels notched, and drive a live device preview
   so the number is never read in isolation. Keyboard: arrows ±1, shift ±10,
   Home/End to the ends. prefers-reduced-motion drops the springs, not the
   function. */
(function () {
  const { useState, useRef, useEffect } = React;

  const MODES = [
    { k: 'responsive', l: 'Responsive', d: 'Restacks for mobile. Columns collapse, type and spacing retune at the breakpoint.', note: 'Adds build time — every block gets a mobile rule.' },
    { k: 'scalable', l: 'Scalable', d: 'One layout that shrinks proportionally. No restacking; images and type scale down together.', note: 'Fast, but small type can get tight on narrow screens.' },
    { k: 'fixed', l: 'Fixed', d: 'A single fixed width. Mobile clients will zoom or scroll.', note: 'Quickest build. Only use when the audience is desktop-first.' },
  ];

  function ModeIcon({ k }) {
    const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    if (k === 'responsive') return (
      <svg width="34" height="26" viewBox="0 0 34 26" {...s}><rect x="1" y="2" width="22" height="15" rx="1.5" /><path d="M8 21h8M12 17v4" /><rect x="25" y="8" width="8" height="15" rx="1.5" /><path d="M28 20h2" /></svg>);
    if (k === 'scalable') return (
      <svg width="34" height="26" viewBox="0 0 34 26" {...s}><rect x="4" y="2" width="26" height="17" rx="1.5" /><path d="M13 23h8M17 19v4M8 6l3 3M8 6v3M8 6h3M26 15l-3-3M26 15v-3M26 15h-3" /></svg>);
    return (
      <svg width="34" height="26" viewBox="0 0 34 26" {...s}><rect x="4" y="2" width="26" height="17" rx="1.5" /><path d="M13 23h8M17 19v4M8 6h3M8 6v3M26 15h-3M26 15v-3" /></svg>);
  }

  /* Target clients. Multi-select; none selected is valid and means the build
     follows the universal email rules with no client-specific tuning. */
  const CLIENTS = [
    { k: 'apple', l: 'Apple Mail', d: 'Full modern CSS — tuned for dark mode, retina images and web fonts.', why: 'renders almost anything', does: ['Dark-mode colours', 'Retina images', 'Web fonts'] },
    { k: 'gmail', l: 'Gmail', d: 'Strips some styles and clips large emails — keeps styles inline and the file small.', why: 'strips styles, clips at 102KB', does: ['Inline styles', 'Under 102KB'] },
    { k: 'outlook', l: 'Outlook', d: 'Uses Word to render on Windows — adds Outlook-only fixes for buttons and spacing.', why: 'renders with Word on Windows', does: ['Bulletproof buttons', 'Table spacing', '120 DPI scaling (planned)', 'Outlook-only fixes'] },
  ];
  function ClientIcon({ k }) {
    const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    if (k === 'apple') return (
      <svg width="30" height="24" viewBox="0 0 30 24" {...s}><rect x="2" y="5" width="22" height="15" rx="2" /><path d="m2.8 6.2 10.2 7.6 10.2-7.6" /><circle cx="25" cy="5" r="3.2" fill="currentColor" stroke="none" /></svg>);
    if (k === 'gmail') return (
      <svg width="30" height="24" viewBox="0 0 30 24" {...s}><path d="M4 7v13h4V11l7 5 7-5v9h4V7" /><path d="M4 7l11 8 11-8" /><path d="M4 7a2 2 0 0 1 3.2-1.6L15 11l7.8-5.6A2 2 0 0 1 26 7" /></svg>);
    return (
      <svg width="30" height="24" viewBox="0 0 30 24" {...s}><rect x="11" y="5" width="16" height="13" rx="1.5" /><path d="m11.6 6 7.4 5.5L26.4 6" /><rect x="3" y="3" width="11" height="17" rx="2" fill="var(--surface)" /><circle cx="8.5" cy="11.5" r="2.8" /></svg>);
  }

  /* Tactile dial: a notched track, a sprung knob and a readout that counts. */
  function Dial({ dial, label, ico, min, max, value, onChange, detents, disabled, sub }) {
    const track = useRef(null);
    const [drag, setDrag] = useState(false);
    const [pulse, setPulse] = useState(0);
    const [shown, setShown] = useState(value);
    const last = useRef(value);
    const stopDrag = useRef(null);
    const animate = typeof window.requestAnimationFrame === 'function' &&
      !(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    useEffect(() => () => { if (stopDrag.current) stopDrag.current(); }, []);
    useEffect(() => {
      if (disabled) {
        if (stopDrag.current) stopDrag.current();
        setDrag(false);
      }
    }, [disabled]);

    /* count the readout toward the value instead of jumping */
    useEffect(() => {
      if (!animate) { setShown(value); return; }
      let raf, active = true, from = shown;
      const t0 = window.performance.now();
      const step = (t) => {
        if (!active) return;
        const k = Math.min(1, (t - t0) / 220);
        const e = 1 - Math.pow(1 - k, 3);
        setShown(Math.round(from + (value - from) * e));
        if (k < 1) raf = window.requestAnimationFrame(step);
      };
      raf = window.requestAnimationFrame(step);
      return () => {
        active = false;
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(raf);
      };
    }, [value, animate]);

    const snap = (v) => {
      const near = detents.find((d) => Math.abs(d - v) <= 6);
      return near != null ? near : v;
    };
    const set = (raw, doSnap) => {
      const v = Math.max(min, Math.min(max, Math.round(raw)));
      const out = doSnap ? snap(v) : v;
      /* notch: pulse the knob on every 10px boundary and on a detent */
      if (Math.floor(out / 10) !== Math.floor(last.current / 10) || (detents.includes(out) && out !== last.current)) setPulse((p) => p + 1);
      last.current = out;
      onChange(out);
    };
    const fromX = (x) => {
      const r = track.current.getBoundingClientRect();
      return min + ((x - r.left) / r.width) * (max - min);
    };
    const down = (e) => {
      if (disabled) return;
      e.preventDefault();
      track.current.focus();
      if (stopDrag.current) stopDrag.current();
      setDrag(true);
      set(fromX(e.clientX), true);
      const move = (ev) => set(fromX(ev.clientX), true);
      const clean = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        stopDrag.current = null;
      };
      const up = () => { setDrag(false); clean(); };
      stopDrag.current = clean;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    };
    const key = (e) => {
      if (disabled) return;
      const d = e.shiftKey ? 10 : 1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); set(value + d); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); set(value - d); }
      else if (e.key === 'Home') { e.preventDefault(); set(min); }
      else if (e.key === 'End') { e.preventDefault(); set(max); }
    };
    const pct = ((value - min) / (max - min)) * 100;
    const ticks = [];
    for (let v = Math.ceil(min / 10) * 10; v <= max; v += 10) ticks.push(v);

    return (
      <div className={'stdl-dial' + (drag ? ' drag' : '') + (disabled ? ' off' : '')}>
        <div className="stdl-dial-h">
          <span className="stdl-dial-i">{ico}</span>
          <span className="stdl-dial-l">{label}</span>
          <span className="stdl-dial-v"><b key={pulse} className="stdl-bump">{animate ? shown : value}</b><i>px</i></span>
        </div>
        <div className="stdl-track" data-dial={dial} ref={track} onPointerDown={down} role="slider" tabIndex={disabled ? -1 : 0}
          aria-label={label} aria-valuemin={min} aria-valuemax={max} aria-valuenow={value} aria-valuetext={value + ' px'} aria-disabled={disabled || undefined} onKeyDown={key}>
          <div className="stdl-ticks">
            {ticks.map((t) => (
              <i key={t} className={(detents.includes(t) ? 'det' : '') + (t <= value ? ' on' : '')} style={{ left: ((t - min) / (max - min)) * 100 + '%' }}></i>
            ))}
          </div>
          <div className="stdl-rail"><span className="stdl-fill" style={{ width: pct + '%' }}></span></div>
          <div className="stdl-knob" style={{ left: pct + '%' }}>
            <span key={pulse} className="stdl-knob-in"></span>
          </div>
        </div>
        <div className="stdl-dets">
          {detents.map((d) => (
            <button key={d} type="button" disabled={disabled} className={value === d ? 'on' : ''} onClick={() => set(d)}
              style={{ left: ((d - min) / (max - min)) * 100 + '%' }}>{d}</button>
          ))}
        </div>
        {sub && <div className="stdl-sub">{sub}</div>}
      </div>);
  }

  /* Live preview: both frames resize with the dials, on one shared scale. */
  function Preview({ mode, desk, mob }) {
    const S = 0.22;
    const bars = (w) => (
      <React.Fragment>
        <span className="stdl-pv-hero"></span>
        <span className="stdl-pv-row" style={{ width: '82%' }}></span>
        <span className="stdl-pv-row" style={{ width: '64%' }}></span>
        {mode === 'responsive' && w < 500
          ? <React.Fragment><span className="stdl-pv-col"></span><span className="stdl-pv-col"></span></React.Fragment>
          : <span className="stdl-pv-cols"><i></i><i></i></span>}
        <span className="stdl-pv-cta"></span>
      </React.Fragment>);
    const mobInner = mode === 'fixed' ? desk : mob;
    const scaleK = mode === 'scalable' ? mob / desk : 1;
    return (
      <div className="stdl-pv">
        <div className="stdl-pv-d">
          <div className="stdl-pv-screen" style={{ width: desk * S }}>{bars(desk)}</div>
          <span className="stdl-pv-cap">Desktop · {desk}px</span>
        </div>
        <div className="stdl-pv-m">
          <div className={'stdl-pv-phone' + (mode === 'fixed' ? ' clip' : '')} style={{ width: mob * S }}>
            <div className="stdl-pv-screen in" style={mode === 'scalable'
              ? { width: desk * S, transform: 'scale(' + scaleK + ')', transformOrigin: 'top left' }
              : { width: mobInner * S }}>
              {bars(mode === 'scalable' ? desk : mobInner)}
            </div>
          </div>
          <span className="stdl-pv-cap">{mode === 'fixed' ? 'Mobile · zooms' : mode === 'scalable' ? 'Mobile · ' + Math.round(scaleK * 100) + '% scale' : 'Mobile · ' + mob + 'px'}</span>
        </div>
      </div>);
  }

  function StdLayout({ value, onChange, clamp = clampLayout }) {
    const v = clamp({ mode: 'responsive', desktop: 600, mobile: 375, clients: ['apple', 'gmail', 'outlook'], ...(value || {}) });
    const tog = (k) => {
      const on = v.clients.includes(k);
      put({ clients: on ? v.clients.filter((c) => c !== k) : CLIENTS.map((c) => c.k).filter((c) => c === k || v.clients.includes(c)) });
    };
    const picked = CLIENTS.filter((c) => v.clients.includes(c.k));
    const put = (patch) => onChange(clamp({ ...v, ...patch }));
    const m = MODES.find((x) => x.k === v.mode) || MODES[0];
    return (
      <div className="stdl" data-layout-mode={v.mode} data-optimized-for={picked.map((c) => c.k).join(',')}>
        <div className="stdl-h">Layout</div>
        <div className="stdl-modes" role="radiogroup" aria-label="Layout">
          {MODES.map((x) => (
            <button key={x.k} type="button" role="radio" aria-checked={v.mode === x.k}
              className={'stdl-mode' + (v.mode === x.k ? ' on' : '')} onClick={() => put({ mode: x.k })} title={x.d}>
              <span className="stdl-mode-i"><ModeIcon k={x.k} /></span>
              <span className="stdl-mode-l">{x.l}</span>
              <span className="stdl-mode-c"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.2 5 8.6l4.5-5" /></svg></span>
            </button>
          ))}
        </div>
        <p className="stdl-note" key={m.k}><b>{m.l + '.'}</b> {m.d}<small>{m.note}</small></p>

        <div className="stdl-h" style={{ marginTop: 18 }}>Optimized for</div>
        <div className="stdl-modes" role="group" aria-label="Optimized for">
          {CLIENTS.map((x) => {
            const on = v.clients.includes(x.k);
            return (
              <button key={x.k} type="button" aria-pressed={on} className={'stdl-mode' + (on ? ' on' : '')}
                onClick={() => tog(x.k)} title={x.d}>
                <span className="stdl-mode-i"><ClientIcon k={x.k} /></span>
                <span className="stdl-mode-l">{x.l}</span>
                <span className="stdl-mode-c"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.2 5 8.6l4.5-5" /></svg></span>
              </button>);
          })}
        </div>
        {/* One row per picked client: what it is known for, then what the build
            will do about it as scannable chips — instead of a run-on paragraph. */}
        <div className="stdl-opt" key={v.clients.join() || 'none'}>
          {!picked.length &&
            <div className="stdl-opt-none">No client tuning — the build follows the universal email rules only. Pick an app above to add its fixes.</div>}
          {picked.map((c) => (
            <div key={c.k} className="stdl-opt-r">
              <span className="stdl-opt-i"><ClientIcon k={c.k} /></span>
              <span className="stdl-opt-t"><b>{c.l}</b><i>{c.why}</i></span>
              <span className="stdl-opt-d"><i>Adds</i>{c.does.map((d, i) => <React.Fragment key={d}>{i > 0 && <s aria-hidden="true"></s>}<em>{d}</em></React.Fragment>)}</span>
            </div>
          ))}
          {!!picked.length && <div className="stdl-opt-f">
            {picked.length === CLIENTS.length
              ? 'Balanced for all three — the safest default.'
              : 'Other email apps still work, they just aren\u2019t tuned for.'}
          </div>}
        </div>

        <div className="stdl-dials">
          <Dial dial="desktop" label="Desktop width" min={480} max={800} value={v.desktop} detents={[600, 640, 700]}
            onChange={(n) => put({ desktop: n, mobile: Math.min(v.mobile, n) })}
            ico={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /></svg>} />
          <Dial dial="mobile" label="Mobile width" min={320} max={Math.min(480, v.desktop)} value={v.mobile} detents={[320, 375, 414]}
            disabled={v.mode === 'fixed'}
            sub={v.mode === 'fixed' ? 'Fixed layouts ship one width — mobile clients render the desktop size.' : v.mode === 'scalable' ? 'The desktop layout scales down to this width.' : 'Breakpoint the layout restacks below.'}
            onChange={(n) => put({ mobile: Math.min(n, v.desktop) })}
            ico={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="7" y="2.5" width="10" height="19" rx="2" /><path d="M11 18.5h2" /></svg>} />
        </div>

        <Preview mode={v.mode} desk={v.desktop} mob={v.mobile} />
      </div>);
  }

  window.StdLayout = StdLayout;
})();
